/**
 * The one decision every DPoP path depends on: does this session use DPoP, and may it ever fall back
 * to the B2C token as Bearer? Pure - no imports - so the truth table is testable on its own
 * (tests/probes/dpop-modules.spec.ts); dpop-authorization.ts feeds it the live environment.
 *
 *  - off         : DPoP is not in play (build flag off, or not a B2C session). Bearer, as always.
 *  - dpop        : use DPoP.
 *  - unavailable : strict mode is on but this browser cannot hold a DPoP key. No Bearer fallback.
 *
 * Strict mode (VITE_dpopStrict) is what makes Gorelo Web DPoP-only: no Bearer fallback, and the
 * per-user kill switch is ignored. Without it, any DPoP problem quietly falls back to Bearer.
 */
export interface DpopEnvironment {
    /** VITE_dpopEnabled. */
    enabled: boolean;
    /** VITE_dpopStrict. Has no effect unless `enabled`. */
    strict: boolean;
    /** Signed in through Azure AD B2C (magic-link sessions carry gateway-minted tokens instead). */
    b2cSession: boolean;
    /** Secure context, WebCrypto and IndexedDB are all available. */
    capable: boolean;
    /** localStorage['gorelo.dpop.disabled'] === 'true'. */
    killSwitch: boolean;
}

export type DpopMode = 'off' | 'dpop' | 'unavailable';

export function dpopMode(env: DpopEnvironment): DpopMode {
    if (!env.enabled || !env.b2cSession) return 'off';
    if (env.strict) return env.capable ? 'dpop' : 'unavailable';
    return env.capable && !env.killSwitch ? 'dpop' : 'off';
}

export function isStrict(env: DpopEnvironment): boolean {
    return env.enabled && env.strict;
}

/** Thrown in strict mode instead of falling back to the B2C token. */
export class DpopUnavailableError extends Error {
    constructor(message = 'A secure (DPoP) session with the Gorelo gateway could not be established.') {
        super(message);
        this.name = 'DpopUnavailableError';
    }
}
